import { useState, useMemo, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Clock,
  CheckCircle,
  XCircle,
  Loader,
  ChevronDown,
  ChevronRight,
  History,
  Search,
  Filter,
  ArrowUpDown,
  FileText,
  Terminal,
  Rocket,
  Shield,
  GitMerge,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
  AlertCircle,
} from 'lucide-react'
import api from '../lib/api'

// ─── Types ───────────────────────────────────────────────────────────────

interface AgentStep {
  name: string
  status: string
  result?: string
  startedAt?: string
  completedAt?: string
}

interface Phase {
  name: string
  status: 'pending' | 'running' | 'completed' | 'failed'
  result?: string
  startedAt?: string
  completedAt?: string
  steps?: AgentStep[]
}

interface Workflow {
  id: string
  prompt: string
  status: 'completed' | 'failed' | 'running' | 'pending' | 'cancelled'
  progress: number
  phases: Phase[]
  startedAt: string
  completedAt?: string
  error?: string
}

interface ListResponse {
  data: Workflow[]
  total?: number
}

// ─── Constants ───────────────────────────────────────────────────────────

const ITEMS_PER_PAGE = 20

const phaseIcons: Record<string, any> = {
  planning: FileText,
  analysis: Terminal,
  coding: Rocket,
  testing: Shield,
  review: GitMerge,
  deployment: Rocket,
}
const phaseIconDefault = Terminal

const statusIcons: Record<string, any> = {
  pending: Clock,
  running: Loader,
  completed: CheckCircle,
  failed: XCircle,
  cancelled: AlertCircle,
}

const statusColors: Record<string, string> = {
  pending: 'text-gray-400',
  running: 'text-blue-400',
  completed: 'text-green-400',
  failed: 'text-red-400',
  cancelled: 'text-gray-400',
}

const statusBadgeColors: Record<string, string> = {
  pending: 'bg-gray-700 text-gray-300',
  running: 'bg-blue-900 text-blue-300 animate-pulse',
  completed: 'bg-green-900 text-green-300',
  failed: 'bg-red-900 text-red-300',
  cancelled: 'bg-gray-700 text-gray-400',
}

type SortOption = 'newest' | 'oldest' | 'status'
type StatusFilter = 'all' | 'completed' | 'failed' | 'running' | 'pending' | 'cancelled'

// ─── Helpers ─────────────────────────────────────────────────────────────

function formatDuration(start: string, end?: string): string {
  const startTime = new Date(start).getTime()
  const endTime = end ? new Date(end).getTime() : Date.now()
  const ms = endTime - startTime
  if (ms < 0) return '—'
  const hours = Math.floor(ms / 3_600_000)
  const minutes = Math.floor((ms % 3_600_000) / 60_000)
  const seconds = Math.floor((ms % 60_000) / 1000)
  if (hours > 0) return `${hours}h ${minutes}m`
  if (minutes > 0) return `${minutes}m ${seconds}s`
  return `${seconds}s`
}

function truncate(str: string, maxLen: number): string {
  if (str.length <= maxLen) return str
  return str.slice(0, maxLen) + '...'
}

function getStatusSortValue(status: string): number {
  const order: Record<string, number> = { running: 0, pending: 1, completed: 2, failed: 3, cancelled: 4 }
  return order[status] ?? 5
}

// ─── Phase Detail Component ──────────────────────────────────────────────

function PhaseDetail({ phase, index }: { phase: Phase; index: number }) {
  const [expanded, setExpanded] = useState(false)
  const [stepsExpanded, setStepsExpanded] = useState(false)
  const Icon = phaseIcons[phase.name.toLowerCase()] ?? phaseIconDefault
  const StatusIcon = statusIcons[phase.status] ?? Clock

  return (
    <div className="border border-gray-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-800 hover:bg-gray-750 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500 font-mono w-6">#{index + 1}</span>
          <Icon className="w-4 h-4 text-orange-400" />
          <span className="text-sm font-medium">{phase.name}</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <StatusIcon className={`w-3.5 h-3.5 ${statusColors[phase.status]}`} />
            <span className={`text-xs capitalize ${statusColors[phase.status]}`}>
              {phase.status}
            </span>
          </div>
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-500" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="px-4 py-3 bg-gray-850 border-t border-gray-700 text-sm space-y-2">
          {phase.startedAt && (
            <div className="flex justify-between">
              <span className="text-gray-500">开始时间</span>
              <span className="text-gray-300">{new Date(phase.startedAt).toLocaleString()}</span>
            </div>
          )}
          {phase.completedAt && (
            <div className="flex justify-between">
              <span className="text-gray-500">完成时间</span>
              <span className="text-gray-300">{new Date(phase.completedAt).toLocaleString()}</span>
            </div>
          )}
          {phase.startedAt && phase.completedAt && (
            <div className="flex justify-between">
              <span className="text-gray-500">耗时</span>
              <span className="text-orange-400">{formatDuration(phase.startedAt, phase.completedAt)}</span>
            </div>
          )}
          {phase.result && (
            <div className="mt-2">
              <span className="text-gray-500 block mb-1">结果</span>
              <p className="text-gray-300 bg-gray-800 rounded p-2 text-xs font-mono whitespace-pre-wrap max-h-32 overflow-y-auto">
                {phase.result}
              </p>
            </div>
          )}
          {phase.steps && phase.steps.length > 0 && (
            <div className="mt-2">
              <button
                onClick={(e) => { e.stopPropagation(); setStepsExpanded(!stepsExpanded) }}
                className="flex items-center gap-1 text-xs text-orange-400 hover:text-orange-300"
              >
                {stepsExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                Agent 步骤 ({phase.steps.length})
              </button>
              {stepsExpanded && (
                <div className="mt-2 space-y-1 ml-2">
                  {phase.steps.map((step, stepIdx) => {
                    const StepIcon = statusIcons[step.status] ?? Clock
                    return (
                      <div key={stepIdx} className="flex items-center gap-2 text-xs py-1">
                        <StepIcon className={`w-3 h-3 ${statusColors[step.status]}`} />
                        <span className="text-gray-300">{step.name}</span>
                        <span className={`ml-auto capitalize ${statusColors[step.status]}`}>{step.status}</span>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}
          {!phase.result && phase.status === 'pending' && (
            <p className="text-gray-600 italic">等待启动...</p>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Progress Bar ────────────────────────────────────────────────────────

function ProgressBar({ progress, status }: { progress: number; status: string }) {
  const barColor =
    status === 'completed'
      ? 'bg-green-500'
      : status === 'failed'
        ? 'bg-red-500'
        : 'bg-orange-500'

  return (
    <div className="w-full">
      <div className="flex justify-between text-xs mb-1">
        <span className="text-gray-400">进度</span>
        <span className="font-semibold text-white">{Math.round(progress)}%</span>
      </div>
      <div className="w-full bg-gray-700 rounded-full h-2 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-700 ${barColor}`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  )
}

// ─── Workflow Detail Row ─────────────────────────────────────────────────

function WorkflowDetail({ workflow }: { workflow: Workflow }) {
  return (
    <div className="space-y-4 mt-4 pt-4 border-t border-gray-700">
      {/* Full prompt */}
      <div>
        <span className="text-gray-500 text-xs uppercase tracking-wider block mb-1">完整提示词</span>
        <p className="text-gray-300 text-sm bg-gray-800 rounded p-3 whitespace-pre-wrap">{workflow.prompt}</p>
      </div>

      {/* Error display */}
      {workflow.error && (
        <div className="p-3 bg-red-900/30 border border-red-800 rounded-lg">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-400" />
            <span className="text-red-300 text-sm font-medium">错误</span>
          </div>
          <p className="text-red-300 text-sm mt-1 ml-6">{workflow.error}</p>
        </div>
      )}

      {/* Progress */}
      <div className="bg-gray-800 rounded p-3 border border-gray-700">
        <ProgressBar progress={workflow.progress} status={workflow.status} />
      </div>

      {/* Phases */}
      <div>
        <h4 className="text-sm font-semibold mb-2 text-gray-300">阶段 ({workflow.phases.length})</h4>
        <div className="space-y-2">
          {workflow.phases.map((phase, idx) => (
            <PhaseDetail key={`${phase.name}-${idx}`} phase={phase} index={idx} />
          ))}
          {workflow.phases.length === 0 && (
            <p className="text-gray-500 text-sm text-center py-4">暂无阶段数据</p>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Main Page Component ─────────────────────────────────────────────────

export default function WorkflowHistory() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [searchText, setSearchText] = useState('')
  const [sortOption, setSortOption] = useState<SortOption>('newest')
  const [currentPage, setCurrentPage] = useState(1)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  const { data, isLoading, error } = useQuery({
    queryKey: ['workflow-history'],
    queryFn: () => api.get<ListResponse>('/task/list').then(res => res.data),
    refetchInterval: 30000,
  })

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [statusFilter, searchText, sortOption, dateFrom, dateTo])

  const workflows = useMemo(() => {
    const items: Workflow[] = data?.data ?? []
    let filtered = items

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(w => w.status === statusFilter)
    }

    // Search by prompt
    if (searchText.trim()) {
      const query = searchText.toLowerCase()
      filtered = filtered.filter(w => w.prompt.toLowerCase().includes(query))
    }

    // Date range filter
    if (dateFrom) {
      const from = new Date(dateFrom).getTime()
      filtered = filtered.filter(w => new Date(w.startedAt).getTime() >= from)
    }
    if (dateTo) {
      // Set to end of day (23:59:59.999) in local timezone
      const to = new Date(dateTo)
      to.setHours(23, 59, 59, 999)
      filtered = filtered.filter(w => new Date(w.startedAt).getTime() <= to.getTime())
    }

    // Sorting
    switch (sortOption) {
      case 'newest':
        filtered.sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime())
        break
      case 'oldest':
        filtered.sort((a, b) => new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime())
        break
      case 'status':
        filtered.sort((a, b) => getStatusSortValue(a.status) - getStatusSortValue(b.status))
        break
    }

    return filtered
  }, [data, statusFilter, searchText, sortOption, dateFrom, dateTo])

  // Pagination
  const totalPages = Math.ceil(workflows.length / ITEMS_PER_PAGE)
  const paginatedWorkflows = workflows.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  )

  const toggleExpand = (id: string) => {
    setExpandedId(prev => prev === id ? null : id)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <History className="w-6 h-6 text-orange-500" />
        <h2 className="text-2xl font-bold">工作流历史</h2>
      </div>

      {/* Filters */}
      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
        <div className="flex flex-wrap gap-4 items-end">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs text-gray-400 mb-1">搜索提示词</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="text"
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                placeholder="输入关键词搜索..."
                className="w-full pl-9 pr-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm focus:outline-none focus:border-orange-500"
              />
            </div>
          </div>

          {/* Status filter */}
          <div className="min-w-[140px]">
            <label className="block text-xs text-gray-400 mb-1">
              <Filter className="w-3 h-3 inline mr-1" />
              状态筛选
            </label>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value as StatusFilter)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm focus:outline-none focus:border-orange-500"
            >
              <option value="all">全部</option>
              <option value="completed">已完成</option>
              <option value="failed">已失败</option>
              <option value="running">运行中</option>
              <option value="pending">待执行</option>
              <option value="cancelled">已取消</option>
            </select>
          </div>

          {/* Date from */}
          <div className="min-w-[140px]">
            <label className="block text-xs text-gray-400 mb-1">开始日期</label>
            <input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm focus:outline-none focus:border-orange-500"
            />
          </div>

          {/* Date to */}
          <div className="min-w-[140px]">
            <label className="block text-xs text-gray-400 mb-1">结束日期</label>
            <input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm focus:outline-none focus:border-orange-500"
            />
          </div>

          {/* Sort */}
          <div className="min-w-[140px]">
            <label className="block text-xs text-gray-400 mb-1">
              <ArrowUpDown className="w-3 h-3 inline mr-1" />
              排序
            </label>
            <select
              value={sortOption}
              onChange={e => setSortOption(e.target.value as SortOption)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm focus:outline-none focus:border-orange-500"
            >
              <option value="newest">最新优先</option>
              <option value="oldest">最旧优先</option>
              <option value="status">按状态</option>
            </select>
          </div>

          {/* Reset button */}
          <button
            onClick={() => {
              setStatusFilter('all')
              setSearchText('')
              setSortOption('newest')
              setDateFrom('')
              setDateTo('')
            }}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm transition-colors"
          >
            重置
          </button>
        </div>
      </div>

      {/* Results summary */}
      <div className="flex items-center justify-between text-sm text-gray-400">
        <span>共 {workflows.length} 条记录，第 {currentPage}/{totalPages || 1} 页</span>
      </div>

      {/* Loading / Error */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader className="w-8 h-8 text-orange-500 animate-spin" />
          <span className="ml-3 text-gray-400">加载中...</span>
        </div>
      )}

      {error && (
        <div className="bg-red-900/30 border border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-300">加载失败: {(error as Error).message}</span>
          </div>
        </div>
      )}

      {/* Workflow list */}
      {!isLoading && !error && (
        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
          {/* Table header */}
          <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-gray-700 text-xs font-medium text-gray-300 uppercase tracking-wider">
            <div className="col-span-2">工作流 ID</div>
            <div className="col-span-4">提示词</div>
            <div className="col-span-1">状态</div>
            <div className="col-span-2">进度</div>
            <div className="col-span-1">开始时间</div>
            <div className="col-span-1">耗时</div>
            <div className="col-span-1"></div>
          </div>

          {/* Table body */}
          <div className="divide-y divide-gray-700">
            {paginatedWorkflows.map((workflow) => {
              const StatusIcon = statusIcons[workflow.status] ?? Clock
              const isExpanded = expandedId === workflow.id

              return (
                <div key={workflow.id} className="px-6 py-4 hover:bg-gray-750 transition-colors">
                  <div
                    className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 items-center cursor-pointer"
                    onClick={() => toggleExpand(workflow.id)}
                  >
                    {/* ID */}
                    <div className="md:col-span-2">
                      <span className="text-xs font-mono text-gray-400 break-all">{workflow.id}</span>
                    </div>

                    {/* Prompt */}
                    <div className="md:col-span-4">
                      <p className="text-sm text-gray-300 truncate">{truncate(workflow.prompt, 100)}</p>
                    </div>

                    {/* Status */}
                    <div className="md:col-span-1">
                      <div className="flex items-center gap-1.5">
                        <StatusIcon className={`w-4 h-4 ${statusColors[workflow.status]}`} />
                        <span className={`text-xs capitalize px-2 py-0.5 rounded ${statusBadgeColors[workflow.status]}`}>
                          {workflow.status === 'completed' ? '完成' :
                           workflow.status === 'failed' ? '失败' :
                           workflow.status === 'running' ? '运行中' :
                           workflow.status === 'pending' ? '待执行' : '已取消'}
                        </span>
                      </div>
                    </div>

                    {/* Progress */}
                    <div className="md:col-span-2">
                      <ProgressBar progress={workflow.progress} status={workflow.status} />
                    </div>

                    {/* Start time */}
                    <div className="md:col-span-1">
                      <span className="text-xs text-gray-400">{new Date(workflow.startedAt).toLocaleDateString()}</span>
                    </div>

                    {/* Duration */}
                    <div className="md:col-span-1">
                      <span className="text-xs text-orange-400">
                        {workflow.status === 'completed' || workflow.status === 'failed'
                          ? formatDuration(workflow.startedAt, workflow.completedAt)
                          : formatDuration(workflow.startedAt)}
                      </span>
                    </div>

                    {/* Expand button */}
                    <div className="md:col-span-1 flex justify-end">
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <WorkflowDetail workflow={workflow} />
                  )}
                </div>
              )
            })}

            {paginatedWorkflows.length === 0 && (
              <div className="py-12 text-center">
                <History className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">没有找到匹配的工作流记录</p>
                <p className="text-gray-600 text-sm mt-1">尝试调整筛选条件</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pagination */}
      {!isLoading && totalPages > 1 && (
        <div className="flex items-center justify-between">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="flex items-center gap-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            上一页
          </button>

          <div className="flex items-center gap-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-8 h-8 rounded text-sm transition-colors ${
                  page === currentPage
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-800 border border-gray-700 text-gray-300 hover:bg-gray-700'
                }`}
              >
                {page}
              </button>
            ))}
          </div>

          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="flex items-center gap-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700 transition-colors"
          >
            下一页
            <ChevronRightIcon className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  )
}
